import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import ModalShell from '../../Attendance/Component/ModalShell';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import {
    createShiftAssignment,
    updateShiftAssignment,
    endShiftAssignment,
} from '../../../../redux/actions/SocietyPortal/ShiftAction/ShiftAction';
import { getEmployeesDropdown } from '../../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import {
    WEEKDAYS,
    toBool,
    todayISO,
    addDays,
    formatDateOnly,
    shiftTimeRange,
} from '../../../../consts/shiftConstants';

// Assign an employee to a shift from a date — POST /api/shift-assignments.
//
// Most assigning happens on the employee form, which closes any existing
// assignment for you. This screen is for what that form refuses: future-dated
// assignments, back-dated corrections, explicit end dates.
//
// Because it does NOT auto-close, and because an open-ended assignment overlaps
// everything after its start, a 409 here is the normal answer for an employee
// who already has a shift. The payload carries the conflicting assignment, so
// the fix is offered inline rather than sending the admin away.

const emptyForm = () => ({
    employee_id: '',
    shift_id: '',
    effective_from: todayISO(),
    openEnded: true,
    effective_to: '',
    weekly_off_days: [0],
    notes: '',
    is_active: true,
});

const fromAssignment = (a) => ({
    employee_id: String(a.employee_id ?? ''),
    shift_id: String(a.shift_id ?? ''),
    effective_from: a.effective_from || todayISO(),
    openEnded: !a.effective_to,
    effective_to: a.effective_to || '',
    weekly_off_days: Array.isArray(a.weekly_off_days) ? a.weekly_off_days : [],
    notes: a.notes || '',
    is_active: toBool(a.is_active),
});

const AssignmentFormModal = ({ isOpen, assignment, onClose, onDone }) => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const isEdit = Boolean(assignment?.assignment_id);

    const { assignmentMutationLoading, shifts, shiftEnums } = useSelector(state => state.ShiftReducer || {});
    const { employeesDropdownData, employeesDropdownLoading } = useSelector(state => state.EmployeeReducer || {});

    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    // Set when the API refuses with ASSIGNMENT_OVERLAP — carries the assignment
    // already covering these dates so the fix can be offered inline.
    const [conflict, setConflict] = useState(null);

    useEffect(() => {
        if (!isOpen) return;
        setForm(assignment?.assignment_id ? fromAssignment(assignment) : emptyForm());
        setErrors({});
        setConflict(null);
        if (societyId) dispatch(getEmployeesDropdown({ societyId }));
    }, [isOpen, assignment, dispatch, societyId]);

    const set = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: undefined }));
        setConflict(null);
    };

    const employeeOptions = useMemo(() => (
        (Array.isArray(employeesDropdownData) ? employeesDropdownData : []).map(e => {
            const name = [e.first_name, e.last_name].filter(Boolean).join(' ').trim()
                || e.email || `Employee #${e.employee_id}`;
            return {
                value: String(e.employee_id),
                label: e.employee_type ? `${name} — ${e.employee_type}` : name,
            };
        })
    ), [employeesDropdownData]);

    // Inactive shifts are rejected with SHIFT_INACTIVE, so keep them out of the
    // picker entirely — except the one an existing assignment already points at.
    const shiftOptions = useMemo(() => (
        (shifts || [])
            .filter(s => toBool(s.is_active) || String(s.shift_id) === String(assignment?.shift_id))
            .map(s => ({
                value: String(s.shift_id),
                label: `${s.shift_code} · ${s.shift_name} (${shiftTimeRange(s)})`,
            }))
    ), [shifts, assignment]);

    const weekdays = shiftEnums?.weekdays?.length
        ? shiftEnums.weekdays.map(w => ({
            ...w,
            short: WEEKDAYS.find(d => d.value === w.value)?.short || w.name?.slice(0, 3),
        }))
        : WEEKDAYS;

    const toggleOffDay = (value) => {
        setForm(prev => {
            const next = prev.weekly_off_days.includes(value)
                ? prev.weekly_off_days.filter(d => d !== value)
                : [...prev.weekly_off_days, value].sort((a, b) => a - b);
            return { ...prev, weekly_off_days: next };
        });
        setErrors(prev => ({ ...prev, weekly_off_days: undefined }));
        setConflict(null);
    };

    const validate = () => {
        const e = {};
        if (!form.employee_id || form.employee_id === 'all') e.employee_id = 'Select an employee';
        if (!form.shift_id || form.shift_id === 'all') e.shift_id = 'Select a shift';
        if (!form.effective_from) e.effective_from = 'Required';
        if (!form.openEnded) {
            if (!form.effective_to) e.effective_to = 'Required, or make it open-ended';
            else if (form.effective_to < form.effective_from) e.effective_to = 'Cannot be before the start date';
        }
        // All seven days off is rejected — that is not an assignment.
        if (form.weekly_off_days.length >= 7) e.weekly_off_days = 'At least one working day is required';
        return e;
    };

    const buildValues = () => ({
        employee_id: Number(form.employee_id),
        shift_id: Number(form.shift_id),
        effective_from: form.effective_from,
        effective_to: form.openEnded ? null : form.effective_to,
        weekly_off_days: form.weekly_off_days,
        notes: form.notes.trim() || null,
        is_active: form.is_active,
    });

    const handleOverlap = (info) => {
        if (info.code !== 'ASSIGNMENT_OVERLAP') return;
        setConflict(info.data?.conflicting_assignment || {});
    };

    const handleSubmit = () => {
        const e = validate();
        if (Object.keys(e).length > 0) {
            setErrors(e);
            return;
        }
        const values = buildValues();

        if (isEdit) {
            // PATCH does not accept employee_id — an assignment belongs to the
            // person it was created for. Everything else is fair game.
            const { employee_id: _unused, ...patchable } = values;
            dispatch(updateShiftAssignment({
                assignment_id: assignment.assignment_id,
                ...patchable,
                onSuccess: (data) => { onClose(); onDone?.(data); },
                onError: handleOverlap,
            }));
            return;
        }

        dispatch(createShiftAssignment({
            ...values,
            onSuccess: (data) => { onClose(); onDone?.(data); },
            onError: handleOverlap,
        }));
    };

    // The conflicting assignment has to stop the day before this one starts.
    const conflictEndDate = form.effective_from ? addDays(form.effective_from, -1) : null;

    const handleEndConflictAndRetry = () => {
        if (!conflict?.assignment_id || !conflictEndDate) return;
        dispatch(endShiftAssignment({
            assignment_id: conflict.assignment_id,
            effective_to: conflictEndDate,
            onSuccess: () => {
                setConflict(null);
                handleSubmit();
            },
        }));
    };

    const inputClass = (field) =>
        `tw-w-full tw-border tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-1 ${errors[field] ? 'tw-border-red-300 focus:tw-ring-red-400' : 'tw-border-gray-200 focus:tw-ring-blue-500'}`;

    const err = (field) => errors[field]
        ? <p className="tw-text-[11px] tw-text-red-500 tw-mt-1 tw-mb-0">{errors[field]}</p>
        : null;

    return (
        <ModalShell
            isOpen={isOpen}
            title={isEdit ? 'Edit assignment' : 'Assign a shift'}
            icon="hugeicons:user-switch"
            onClose={onClose}
            busy={assignmentMutationLoading}
            maxWidth="tw-max-w-lg"
            actions={
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={assignmentMutationLoading}
                    className="tw-px-3 tw-py-1.5 tw-rounded-md tw-bg-blue-600 tw-text-white tw-text-xs tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50"
                >
                    {assignmentMutationLoading ? 'Saving…' : isEdit ? 'Save changes' : 'Assign shift'}
                </button>
            }
        >
            <div className="tw-space-y-3">
                <div>
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Employee *</label>
                    <SearchableDropdown
                        value={form.employee_id}
                        onChange={(v) => set('employee_id', v === 'all' ? '' : v)}
                        options={employeeOptions}
                        loading={employeesDropdownLoading}
                        disabled={isEdit}
                        placeholder="Select an employee"
                    />
                    {isEdit && <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1 tw-mb-0">The employee cannot be changed on an existing assignment.</p>}
                    {err('employee_id')}
                </div>

                <div>
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Shift *</label>
                    <SearchableDropdown
                        value={form.shift_id}
                        onChange={(v) => set('shift_id', v === 'all' ? '' : v)}
                        options={shiftOptions}
                        placeholder="Select a shift"
                    />
                    {isEdit && (
                        <p className="tw-text-[10px] tw-text-amber-600 tw-mt-1 tw-mb-0">
                            Moving someone to a different shift is better done with <strong>Change shift</strong> — editing here rewrites the past too.
                        </p>
                    )}
                    {err('shift_id')}
                </div>

                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Effective from *</label>
                        <input type="date" value={form.effective_from} onChange={(e) => set('effective_from', e.target.value)} className={inputClass('effective_from')} />
                        {err('effective_from')}
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Effective to</label>
                        <input
                            type="date"
                            value={form.effective_to}
                            min={form.effective_from}
                            disabled={form.openEnded}
                            onChange={(e) => set('effective_to', e.target.value)}
                            className={`${inputClass('effective_to')} disabled:tw-bg-gray-50 disabled:tw-text-gray-400`}
                        />
                        {err('effective_to')}
                    </div>
                </div>

                <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-700 tw-cursor-pointer">
                    <input
                        type="checkbox"
                        checked={form.openEnded}
                        onChange={(e) => set('openEnded', e.target.checked)}
                    />
                    Open-ended (runs until it is ended)
                </label>

                <div>
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Weekly offs</label>
                    <div className="tw-flex tw-gap-1.5 tw-flex-wrap">
                        {weekdays.map(d => {
                            const selected = form.weekly_off_days.includes(d.value);
                            return (
                                <button
                                    key={d.value}
                                    type="button"
                                    onClick={() => toggleOffDay(d.value)}
                                    className={`tw-px-2.5 tw-py-1 tw-rounded-md tw-text-[11px] tw-font-medium tw-border ${selected
                                        ? 'tw-bg-gray-700 tw-text-white tw-border-gray-700'
                                        : 'tw-bg-white tw-text-gray-600 tw-border-gray-200 hover:tw-bg-gray-50'}`}
                                >
                                    {d.short}
                                </button>
                            );
                        })}
                    </div>
                    <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1 tw-mb-0">
                        Days this employee does not work. At least one working day is required.
                    </p>
                    {err('weekly_off_days')}
                </div>

                <div>
                    <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Notes</label>
                    <input type="text" value={form.notes} maxLength={255} onChange={(e) => set('notes', e.target.value)} className={inputClass('notes')} />
                </div>

                <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-700 tw-cursor-pointer tw-pt-1">
                    <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
                    Active
                </label>

                {/* 409 recovery — the payload tells us exactly what is in the way */}
                {conflict && (
                    <div className="tw-rounded-md tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-3">
                        <p className="tw-text-xs tw-text-amber-800 tw-mb-2 tw-flex tw-items-start tw-gap-1.5">
                            <Icon icon="mdi:alert-outline" className="tw-mt-0.5 tw-flex-shrink-0" />
                            <span>
                                This employee already has an assignment covering part of that range
                                {conflict.effective_from ? ` (from ${formatDateOnly(conflict.effective_from)}${conflict.effective_to ? ` to ${formatDateOnly(conflict.effective_to)}` : ', open-ended'})` : ''}.
                            </span>
                        </p>
                        {conflict.assignment_id && conflictEndDate && (
                            <button
                                type="button"
                                onClick={handleEndConflictAndRetry}
                                disabled={assignmentMutationLoading}
                                className="tw-px-2.5 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-white tw-bg-amber-600 hover:tw-bg-amber-700 disabled:tw-opacity-50"
                            >
                                End it on {formatDateOnly(conflictEndDate)} and retry
                            </button>
                        )}
                    </div>
                )}
            </div>
        </ModalShell>
    );
};

export default AssignmentFormModal;
